<template>
  <div class="trade-container">
    <button class="close-btn" @click="goToDashboard">✖ CLOSE</button>

    <h2>TRADES</h2>

    <div class="trade-actions">
      <button class="add-trade win" @click="addNewTrade('Win')">Add Win</button>
      <button class="add-trade loss" @click="addNewTrade('Loss')">
        Add Loss
      </button>
    </div>

    <div v-if="loading">Chargement des trades...</div>
    <div v-else-if="error">{{ error }}</div>

    <div class="trade-list">
      <div
        v-for="trade in trades"
        :key="trade.id"
        class="trade"
        @click="editTrade(trade.id)"
      >
        <div v-if="trade.imageUrls && trade.imageUrls.length > 0">
          <img
            v-for="(image, index) in trade.imageUrls"
            :key="index"
            :src="image"
            class="trade-img"
            @click.stop="openImage(image)"
            @mouseover="zoomImage($event)"
            @mouseleave="resetZoom($event)"
          />
        </div>
        <p>{{ trade.note }}</p>
        <span
          :class="{
            win: trade.status === 'Win',
            loss: trade.status === 'Loss',
          }"
        >
          {{ trade.status }}
        </span>
        <button class="delete-btn" @click.stop="handleDeleteTrade(trade.id)">
          🗑 Delete
        </button>
      </div>
    </div>

    <!-- Lightbox pour afficher l'image en grand -->
    <div v-if="zoomedImage" class="lightbox" @click="closeImage">
      <img :src="zoomedImage" class="fullsize-img" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getTrades, addTrade, deleteTrade } from "@/services/firestore";
import { getAuth } from "firebase/auth";

import { computed } from "vue";

export default defineComponent({
  name: "TradeListPage",
  setup() {
    const auth = getAuth();
    const route = useRoute();
    const router = useRouter();
    const trades = ref<any[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const zoomedImage = ref<string | null>(null);

    const tradeDate = computed(() => route.params.date as string | undefined);

    const handleDeleteTrade = async (tradeId: string) => {
      try {
        await deleteTrade(tradeId); // 🔥 Ne pas envoyer `tradeDate`
        trades.value = trades.value.filter((trade) => trade.id !== tradeId);
        console.log("Trade supprimé !");
      } catch (error) {
        console.error("Erreur lors de la suppression du trade :", error);
      }
    };

    const loadTrades = async () => {
      if (!tradeDate.value) {
        error.value = "Erreur : aucune date fournie dans l'URL.";
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        trades.value = await getTrades(tradeDate.value ?? "");
      } catch (err) {
        error.value = "Impossible de charger les trades.";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const addNewTrade = async (status: "Win" | "Loss") => {
      if (!tradeDate.value) {
        console.error("Date non définie, impossible d'ajouter un trade.");
        return;
      }

      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          console.error("Utilisateur non connecté !");
          return;
        }

        const tradeDoc = await addTrade(tradeDate.value ?? "", {
          note: "",
          status,
          imageUrls: [],
        });

        console.log(`Trade ${status} ajouté !`);
        router.push(`/trade-options/${tradeDoc.id}?date=${tradeDate.value}`);
        await loadTrades();
      } catch (error) {
        console.error("Erreur lors de l'ajout du trade :", error);
      }
    };

    const editTrade = (tradeId: string) => {
      router.push(`/trade-options/${tradeId}?date=${tradeDate.value}`);
    };

    const goToDashboard = () => {
      router.push("/dashboard");
    };

    // Ouvrir une image en grand
    const openImage = (image: string) => {
      zoomedImage.value = image;
    };

    // Fermer l'image affichée en grand
    const closeImage = () => {
      zoomedImage.value = null;
    };

    // Zoom au survol
    const zoomImage = (event: MouseEvent) => {
      const img = event.target as HTMLImageElement;
      img.style.transform = "scale(2)";
      img.style.transition = "transform 0.2s ease-in-out";
    };

    // Réinitialiser le zoom
    const resetZoom = (event: MouseEvent) => {
      const img = event.target as HTMLImageElement;
      img.style.transform = "scale(1)";
    };

    onMounted(loadTrades);

    return {
      trades,
      loading,
      error,
      loadTrades,
      addNewTrade,
      editTrade,
      goToDashboard,
      zoomedImage,
      openImage,
      closeImage,
      zoomImage,
      resetZoom,
      handleDeleteTrade,
    };
  },
});
</script>

<style scoped>
.trade-container {
  background: #1a1a1a;
  padding: 20px;
  border-radius: 15px;
  color: white;
  text-align: left;
}
.trade-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}
.add-trade {
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
.add-trade.win {
  background: green;
  color: white;
}
.add-trade.loss {
  background: red;
  color: white;
}
.trade-list {
  margin-top: 10px;
}
.trade {
  background: #3d3d3d;
  padding: 10px;
  margin: 5px 0;
  border-radius: 5px;
  cursor: pointer;
}
.trade-img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.fullsize-img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 10px;
}

/* Bouton de fermeture */
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: red;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
}
.delete-btn {
  background: crimson;
  color: white;
  border: none;
  padding: 5px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;
}

.delete-btn:hover {
  background: darkred;
}
</style>
